import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useListenEvent } from "go-core/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { PosReportApi } from "../../../../../../../../../../services/Api/Organization/types";
import PosReportPreview from "../../components/PosReportPreview";
import { switchToNextPOSReport, switchToPreviousPOSReport } from "../utils";

interface MatchParams {
	reportId: string;
}

const OrganizationSalesPosReportsPreviewPage = (props: RouteComponentProps) => {
	const history = useHistory();
	const [posReport, setPosReport] = useState<PosReportApi | undefined>(undefined);
	const { addFlash } = useFlash();
	const { reportId } = useParams<MatchParams>();
	const organization = useSelector(selectOrganization);
	const params: Record<string, any> = {
		include: "terminal,end_amounts,sub_reports,paids,paids.transaction,paids.client,full,currencies",
	};
	const { t } = useTranslation();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const [doesPreviousPosReportExist, setDoesPreviousPosReportExists] = useState<boolean | undefined>();
	const [doesNextPosReportExist, setDoesNextPosReportExists] = useState<boolean | undefined>();
	const location = useLocation();

	useListenEvent({
		eventName: "keyup",
		callback: (e) => {
			if (e?.key === "ArrowRight" && doesNextPosReportExist) {
				switchToNextPOSReport(reportId, history, organization, location);
			}

			if (e?.key === "ArrowLeft" && doesPreviousPosReportExist) {
				switchToPreviousPOSReport(reportId, history, organization, location);
			}
		},
	});

	useEffect(() => {
		onFetchPosReport();
		fetchPreviousPosReport();
		fetchNextPosReport();
	}, [reportId]);

	const onFetchPosReport = async () => {
		setPosReport(undefined);
		try {
			const res = await apiOrganization.getPosReport(Number(reportId), params);
			setPosReport(res);
			handleChangeTabTitle(`#${res.id} | ${t("modules.pos_report.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchPreviousPosReport = async () => {
		try {
			const res = await apiOrganization.getPreviousPosReport(Number(reportId));
			setDoesPreviousPosReportExists(!!res);
		} catch (err) {
			setDoesPreviousPosReportExists(false);
		}
	};

	const fetchNextPosReport = async () => {
		try {
			const res = await apiOrganization.getNextPosReport(Number(reportId));
			setDoesNextPosReportExists(!!res);
		} catch (err) {
			setDoesNextPosReportExists(false);
		}
	};

	if (!posReport || doesNextPosReportExist === undefined || doesPreviousPosReportExist === undefined) {
		return <LoadingContainer />;
	}

	const handleUpdatePosReport = async () => {
		try {
			const res = await apiOrganization.getPosReport(Number(reportId), params);
			setPosReport(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<PosReportPreview
			handlePaidSave={handleUpdatePosReport}
			report={posReport}
			url={props.match.url}
			handleClose={(report) => setPosReport(report)}
			doesPreviousPosReportExist={doesPreviousPosReportExist}
			doesNextPosReportExist={doesNextPosReportExist}
		/>
	);
};
export default OrganizationSalesPosReportsPreviewPage;
